import React from "react";
import { Link, useParams } from "react-router-dom";
import { Section, Reveal } from "@/components/network/NetworkAtoms";
import { useFundingCta } from "./FundingCta";
import { SECTORS, SECTOR_LIST } from "./sectors";
import { ArrowUpRight, Building2, Stethoscope, Factory, UtensilsCrossed, Globe2 } from "lucide-react";

const ICONS = { property: Building2, healthcare: Stethoscope, manufacturing: Factory, hospitality: UtensilsCrossed };

/**
 * Cross-links to the sector editions. A resolved introducer's slug rides along, so a customer sent
 * to the property edition on someone's link stays credited to them wherever they click. A slug we
 * couldn't resolve is dropped rather than carried around the site.
 */
export default function SectorStrip({ current }) {
  const { slug } = useParams();
  const { introducer } = useFundingCta();
  const suffix = slug && introducer ? `/${slug}` : "";
  const others = SECTOR_LIST.filter((k) => k !== current);

  return (
    <Section
      testId="funding-sectors"
      eyebrow="By sector"
      title={current ? "Other sectors we fund" : "Funding read through your sector"}
      lede={current
        ? "The process is identical across every sector — the funders, the covenants and the questions are not."
        : "The same desk and the same application, written for the questions your sector actually gets asked."}
    >
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {others.map((k, i) => {
          const s = SECTORS[k];
          const Icon = ICONS[k];
          return (
            <Reveal key={k} delay={i * 0.06}>
              <Link to={`${s.path}${suffix}`} data-testid={`sector-link-${k}`}
                    className="group h-full flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] hover:border-[#0055FF]/40 hover:bg-[#0055FF]/[0.06] p-5 transition-colors">
                <Icon className="size-5 text-[#7da9ff]" />
                <div className="mt-4 text-[15px] font-semibold text-white leading-snug">{s.label}</div>
                <div className="mt-1.5 text-[12.5px] text-slate-400">{s.proof[1].value}</div>
                <span className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#7da9ff]">
                  Read that edition
                  <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          );
        })}

        {current && (
          <Reveal delay={others.length * 0.06}>
            <Link to={`/business-funding${suffix}`} data-testid="sector-link-general"
                  className="group h-full flex flex-col rounded-2xl border border-white/10 bg-white/[0.02] hover:border-[#0055FF]/40 hover:bg-[#0055FF]/[0.06] p-5 transition-colors">
              <Globe2 className="size-5 text-[#7da9ff]" />
              <div className="mt-4 text-[15px] font-semibold text-white leading-snug">Every other sector</div>
              <div className="mt-1.5 text-[12.5px] text-slate-400">£25k – £500m+</div>
              <span className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-[#7da9ff]">
                The general edition
                <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          </Reveal>
        )}
      </div>
    </Section>
  );
}
